import React, { useState, useEffect } from 'react';
import { StorageService } from '../services/storageService';
import { NotionService } from '../services/notionService';
import { getNotionHealth } from '../services/availabilityService';
import { GoogleDriveService } from '../services/googleDriveService';

interface SaveOptionsProps {
  topic: string;
  script: string;
  videoLength: string;
  language: string;
  structure: string;
  videoUrl?: string;
}

const SaveOptions: React.FC<SaveOptionsProps> = ({
  topic,
  script,
  videoLength,
  language,
  structure,
  videoUrl,
}) => {
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: '',
  });
  const [autoSavedScript, setAutoSavedScript] = useState(false);
  const [autoSavedVideo, setAutoSavedVideo] = useState(false);
  const [notionReady, setNotionReady] = useState<{ ready: boolean; missing: any } | null>(null);
  const [driveReady, setDriveReady] = useState<{ configured: boolean; hasFolderId: boolean } | null>(null);

  // Check Notion and Drive health on mount
  useEffect(() => {
    getNotionHealth().then(health => {
      setNotionReady({ ready: health.notionReady, missing: health.missing });
    });
    
    // Check Google Drive configuration
    const driveCheck = GoogleDriveService.checkEnvironmentVariables();
    const hasFolderId = !!import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID && 
                       import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID !== '' &&
                       import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID !== 'undefined';
    setDriveReady({
      configured: driveCheck.status === 'success',
      hasFolderId
    });
  }, []);

  // Reset auto-save flags when topic changes (new generation)
  useEffect(() => {
    setAutoSavedScript(false);
    setAutoSavedVideo(false);
  }, [topic]);

  // Auto-save to Notion when script is generated
  useEffect(() => {
    if (script && !autoSavedScript) {
      // Small delay to ensure script is fully rendered
      setTimeout(() => {
        handleSaveToNotion(true, 'script');
        setAutoSavedScript(true);
      }, 500);
    }
  }, [script, autoSavedScript]);

  // Auto-save to Notion when video is generated
  useEffect(() => {
    if (videoUrl && !autoSavedVideo) {
      handleSaveToNotion(true, 'video');
      setAutoSavedVideo(true);
    }
  }, [videoUrl, autoSavedVideo]);

  const handleSaveToNotion = async (isAutoSave = false, saveType: 'script' | 'video' | 'manual' = 'manual') => {
    setSaveStatus({ type: null, message: '' });
    
    const data = {
      topic,
      length: videoLength,
      language,
      structure,
      script,
      videoUrl,
    };

    const success = await NotionService.saveToNotion(data);
    
    if (success) {
      let message = '✅ Notion에 저장되었습니다!';
      if (isAutoSave) {
        if (saveType === 'script') {
          message = '✅ 스크립트가 자동으로 Notion에 저장되었습니다!';
        } else if (saveType === 'video') {
          message = '✅ 비디오 정보가 업데이트되어 Notion에 저장되었습니다!';
        }
      }
      setSaveStatus({ type: 'success', message });
    } else {
      setSaveStatus({ 
        type: 'error', 
        message: '❌ Notion 저장 실패. API 키와 데이터베이스 ID를 확인해주세요.' 
      });
    }
    
    setTimeout(() => setSaveStatus({ type: null, message: '' }), 3000);
  };

  const handleSaveToGoogleDrive = async (isAutoSave = false, saveType: 'script' | 'video' | 'manual' = 'manual') => {
    setSaveStatus({ type: null, message: '' });
    
    const data = {
      topic,
      length: videoLength,
      language,
      structure,
      script,
      timestamp: new Date().toISOString(),
      videoUrl,
    };

    const success = await StorageService.saveToGoogleDrive(data);
    
    if (success) {
      let message = '✅ Google Drive에 저장되었습니다!';
      if (isAutoSave) {
        if (saveType === 'script') {
          message = '✅ 스크립트가 자동으로 Google Drive에 저장되었습니다!';
        } else if (saveType === 'video') {
          message = '✅ 비디오 정보가 업데이트되어 Google Drive에 저장되었습니다!';
        }
      }
      setSaveStatus({ type: 'success', message });
    } else {
      setSaveStatus({ type: 'error', message: 'Google Drive 저장 실패' });
    }
    
    setTimeout(() => setSaveStatus({ type: null, message: '' }), 5000);
  };

  const handleDownload = (format: 'json' | 'md') => {
    const data = {
      topic,
      length: videoLength,
      language,
      structure,
      script,
      timestamp: new Date().toISOString(),
      videoUrl,
    };

    switch (format) {
      case 'json':
        StorageService.exportAsJSON(data);
        setSaveStatus({ type: 'success', message: 'JSON 파일이 다운로드되었습니다!' });
        break;
      case 'md':
        StorageService.exportAsMarkdown(data);
        setSaveStatus({ type: 'success', message: 'Markdown 파일이 다운로드되었습니다!' });
        break;
    }
    
    setTimeout(() => setSaveStatus({ type: null, message: '' }), 3000);
  };

  if (!script) return null;

  return (
    <div className="mt-6 bg-slate-800/60 p-6 rounded-2xl border border-slate-700">
      <h3 className="text-xl font-bold text-white mb-4">💾 스토리 저장 옵션</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Notion Save (Main Feature) */}
        <button
          onClick={() => handleSaveToNotion(false)}
          disabled={!notionReady?.ready}
          title={!notionReady?.ready ? `Notion 미설정: ${[
            notionReady?.missing?.token && "TOKEN",
            notionReady?.missing?.parent && "PARENT_PAGE_ID",
            notionReady?.missing?.version && "VERSION"
          ].filter(Boolean).join(", ")}` : "Notion에 저장"}
          className={`flex items-center justify-center gap-2 px-6 py-4 rounded-lg transition-all shadow-lg border ${
            notionReady?.ready
              ? 'bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white transform hover:scale-105 border-gray-600'
              : 'bg-gray-800 opacity-60 cursor-not-allowed text-gray-500 border-gray-700'
          }`}
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.906c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046 .935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.139c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.839.374-1.54 1.447-1.632z"/>
          </svg>
          <span className="font-semibold">Notion에 저장</span>
        </button>

        {/* Google Drive Save (Secondary) */}
        <button
          onClick={() => handleSaveToGoogleDrive(false)}
          disabled={!driveReady?.configured}
          title={!driveReady?.configured ? 'Google Drive 미설정' : 
                 !driveReady?.hasFolderId ? 'Drive 폴더 ID 미설정' : 'Google Drive에 저장'}
          className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all ${
            driveReady?.configured
              ? 'bg-slate-700 hover:bg-slate-600 text-white'
              : 'bg-gray-800 opacity-60 cursor-not-allowed text-gray-500'
          }`}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7.71 3.52L1.15 15l3.43 5.48L11.14 9l-3.43-5.48zm6.36 10.48l-3.43 5.48h13.72L21 14l-6.93-.02zM22.28 14L15.42 2H8.58l6.86 12h6.84z"/>
          </svg>
          Google Drive (HTML)
        </button>

        {/* Download as JSON */}
        <button
          onClick={() => handleDownload('json')}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
          </svg>
          JSON 다운로드
        </button>

        {/* Download as Markdown */}
        <button
          onClick={() => handleDownload('md')}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Markdown 다운로드
        </button>
      </div>

      {/* Status Message */}
      {saveStatus.type && (
        <div className={`mt-4 p-3 rounded-lg ${
          saveStatus.type === 'success' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
            : 'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}>
          {saveStatus.message}
        </div>
      )}

      {/* Auto-save Info */}
      <div className="mt-4 space-y-2">
        {autoSavedScript && (
          <div className="p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg">
            <p className="text-blue-400 text-sm">
              📝 <strong>스크립트 자동 저장:</strong> 
              스크립트가 생성되어 자동으로 Notion에 저장되었습니다.
            </p>
          </div>
        )}
        
        {autoSavedVideo && (
          <div className="p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
            <p className="text-green-400 text-sm">
              🎥 <strong>비디오 업데이트:</strong> 
              비디오 정보가 추가되어 Notion에 업데이트되었습니다.
            </p>
          </div>
        )}
      </div>

      {/* Notion Info */}
      <div className="mt-4 p-4 bg-slate-900/50 rounded-lg">
        <p className="text-slate-400 text-sm mb-3">
          💡 <strong>자동 저장 기능:</strong> 
          스크립트 생성 시 자동으로 Notion 데이터베이스에 저장됩니다. 
          비디오가 생성되면 Notion 페이지가 업데이트됩니다.
        </p>
      </div>
    </div>
  );
};

export default SaveOptions;